import { get, has } from 'dot-prop';
import { combineLatest, of, Subject, Subscription } from 'rxjs';

import { DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RoutingConfig, SbKeyFigureAdvancedContext } from '@models';
import { SbDataService, SbEventBusService } from '../../services';
import { getObject } from '../../../shared/utils';

import { FormattingFunctions } from '../sb-ag-table/formatting-functions';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';
import { AccountIssuersQuery } from '../../../pages/admin/state/account-issuers/account-issuers.query';
import { TagsQuery } from '../../../features/new-tags/state/tags.query';
import { AcctsQuery } from '../../../shared/state/accts/accts.query';
import { Acct } from '../../../shared/state/accts/acct.model';
import { RouterQuery } from '@datorama/akita-ng-router-store';

export class KeyFigureValue {
    value: string;
    valueClass?: string[];
    valueInlineStyle?: any;
    field?: string;
    // Optional label used for table layout
    label?: string;
    hidden?: boolean;
    rawValue: number;
}
export interface KeyFigureElement {
    key: string;
    hidden?: boolean;
    labelClass?: string[];
    labelInlineStyle?: any;
    values: KeyFigureValue[];
}

@Component({
    selector: 'app-sb-key-figure-advanced',
    templateUrl: './sb-key-figure-advanced.component.html',
    styleUrls: ['./sb-key-figure-advanced.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbKeyFigureAdvancedComponent
    extends BaseWidgetComponent
    implements OnInit, OnDestroy
{
    useLegacyAccountList: boolean | 'balance-list';
    unsubscribe$ = new Subject();
    // fix to reload component when back button is clicked if accountIds query param is present (i.e. if user navigates to account-balance widget from account-balance widget)
    @HostListener('window:popstate', ['$event'])
    onPopState(event) {
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((res) => {
                // if accountIds query param is present, reload component when back navigation is finished
                if (this._routerQuery.getQueryParams('accountIds')) {
                    this.router.navigateByUrl(`${window.location.pathname}`, {
                        onSameUrlNavigation: 'reload',
                        skipLocationChange: true
                    });
                }
            });
    }

    @Input() context: SbKeyFigureAdvancedContext;
    showMarketValueOverTime: boolean = false;

    // Original dataset loaded remotely via URL configuration
    rawData: any;

    dataElements: KeyFigureElement[];
    subscription: Subscription;
    formattingFunctions: FormattingFunctions;
    useShortNumberFormat: boolean;

    constructor(
        dataService: SbDataService,
        localStorageService: LocalStorageService,
        eventBus: SbEventBusService,
        private changeDetectorRef: ChangeDetectorRef,
        dateFormatter: DatePipe,
        numberFormatter: DecimalPipe,
        percentFormatter: PercentPipe,
        activatedRoute: ActivatedRoute,
        private config: AppConfigQuery,
        protected router: Router,
        private translateService: TranslateService,
        private _ownerQuery: LegalEntityQuery,
        private _accountIssuerQuery: AccountIssuersQuery,
        private _tagsQuery: TagsQuery,
        private _accountsQuery: AcctsQuery,
        private _routerQuery: RouterQuery
    ) {
        super(dataService, localStorageService, eventBus, activatedRoute, router);
        this.responseHandler = this.setData;
        this.autoTriggerDataLoad = true;
        this.formattingFunctions = new FormattingFunctions(
            dateFormatter,
            numberFormatter,
            percentFormatter,
            translateService
        );
    }

    ngOnInit() {
        this.useLegacyAccountList = this.config.useLegacyAccountList;
        this.showMarketValueOverTime =
            this.config.get().restrictDashboardChartView === 'marketValue';
        super.ngOnInit();
    }

    getBulletField(): string {
        return this.context.bulletField || 'key';
    }

    getBulletCssClasses(key?: any, element?: any, index?) {
        const cssClasses: string[] = [];
        if (this.context.bullets) {
            cssClasses.push('sb-circle');
        }

        if (this.context.bulletColors) {
            const bulletColorKey = key ? key.toUpperCase() : 'EMPTY';
            let colorFound = false;
            const color = this.context.bulletColors[bulletColorKey];
            if (color) {
                cssClasses.push(color);
                colorFound = true;
            }
            if (!colorFound) {
                const defaultColor = this.context.bulletColors['DEFAULT'];
                if (defaultColor) {
                    cssClasses.push(defaultColor);
                }
            }
        }
        return cssClasses;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    getKeyFigureValue(key, value): KeyFigureValue {
        const columnFormatting = this.context.columnFormatting || {};
        const types = columnFormatting[key] || [];
        const cellClassRules = this.formattingFunctions.getCellClassRulesFromCellTypes(
            types,
            value
        );
        const formattedText = this.formattingFunctions.getFormattedTextFromCellTypes(
            this.context,
            types,
            value
        );

        return {
            value: formattedText,
            rawValue: value,
            valueClass: this.getCssClass(
                cellClassRules,
                'value',
                this.context.valueClass
            ),
            valueInlineStyle: this.context.valueInlineStyle || {}
        };
    }

    getKeyFigureElement(key): KeyFigureElement {
        return {
            key: key ? this.translateService.instant(key) : key,
            values: [],
            labelClass: this.getCssClass([], 'label', this.context.labelClass),
            labelInlineStyle: this.context.labelInlineStyle || {}
        };
    }

    isObject(value): boolean {
        return typeof value === 'object' && value !== null;
    }

    getColumnConfiguration() {
        return this.context.columnConfiguration;
    }

    getDefaultAlignment(index): string {
        return this.context.columnAlignment && this.context.columnAlignment[index];
    }

    abbreviate(text: string) {
        return text != null ? text.toUpperCase().substring(0, 2) : '';
    }

    public trackByFunction(index, item) {
        if (!item) {
            return index;
        }
        return index;
    }

    private setData(responseData: any) {
        // TODO: Seems like we sometimes pass data cache object instead of data array?
        // This is a temporary fix to handle this
        if (responseData.data) {
            responseData = responseData.data;
        }
        this.rawData = responseData;
        this.setDataElements(responseData);
    }

    setDataElements(data) {
        if (data) {
            const filteredData = data.filter((element) => {
                return (
                    this.context.columnFilter === undefined ||
                    this.context.columnFilter.indexOf(element[this.getBulletField()]) > -1
                );
            });

            const sortedFilterData =
                this.context.columnFilter && this.context.columnFilter.length > 0
                    ? filteredData.sort(
                          this.columnFilterComparator(this.context.columnFilter)
                      )
                    : filteredData;
            const elements = sortedFilterData.map((element) => {
                const keyValue = element[this.getBulletField()];
                if (element.value) {
                    return Object.assign({}, this.getKeyFigureElement(keyValue), {
                        values: [this.getKeyFigureValue(keyValue, element.value)]
                    });
                } else if (element.values) {
                    const values = element.values.map((value) => {
                        return this.getKeyFigureValue(keyValue, value);
                    });
                    return Object.assign({}, this.getKeyFigureElement(keyValue), {
                        values
                    });
                } else if (element instanceof Object) {
                    if (this.context.layout === 'table') {
                        return this.getKeyValueElementsForTableLayout(element);
                    } else {
                        return this.getKeyValueElementFromBulletLayout(keyValue, element);
                    }
                } else {
                    console.error('Element value is missing');
                    return [];
                }
            });
            const hiddenFields = this.context.columnConfiguration
                ? this.context.columnConfiguration
                      .filter((cc) => cc.hidden)
                      .map((cc) => cc.field)
                : [];
            this.dataElements = [
                ...elements.reduce((acc, kfe: KeyFigureElement) => {
                    // Mark key figure element as hidden if configured to be.
                    // Same for nested elements (using field names).
                    if (Array.isArray(kfe.values)) {
                        kfe.values.forEach((v) => {
                            if (hiddenFields.includes(v.field)) {
                                v.hidden = true;
                            }
                        });
                    }
                    return acc.concat(
                        hiddenFields.includes(kfe.key)
                            ? Object.assign({ hidden: true }, kfe)
                            : kfe
                    );
                }, [])
            ];
            // if account-balance widget...
            if (this.context.widgetId === 'account-balance') {
                // ...sort accounts alphanumerically to allow defining sort order using column name, e.g 1. Cash, 2. Equity, 3. Fixed Income etc. https://swimbird.atlassian.net/browse/SWIM-5314
                this.dataElements.sort((a, b) =>
                    a.key.localeCompare(b.key, 'en', { numeric: true })
                );
            }
            this.changeDetectorRef.markForCheck();
        }
    }

    getKeyValueElementsForTableLayout(element: any): KeyFigureElement {
        const values = this.context.columnConfiguration.map((c) => {
            const value = has(element, c.field) ? get(element, c.field) : '';
            const kfv = this.getKeyFigureValue(c.field, value);
            return Object.assign({}, kfv, {
                field: c.field,
                label: c.label ? c.label : c.field
            });
        });
        return Object.assign({}, this.getKeyFigureElement('key'), {
            values: values
        });
    }

    // Generator function for comparing data elements (with 'key' with values in columnfilter)
    columnFilterComparator(columnFilter: string[]) {
        // Return comparator function
        // Assumes values match column filter and gets sorted by index order
        return function (a, b) {
            if (
                !a.hasOwnProperty('key') ||
                !b.hasOwnProperty('key') ||
                columnFilter === undefined
            ) {
                return 0;
            }
            const cf = columnFilter;
            if (cf.indexOf(a.key) < cf.indexOf(b.key)) {
                return -1;
            }
            if (cf.indexOf(a.key) > cf.indexOf(b.key)) {
                return 1;
            }
            return 0;
        };
    }

    private getKeyValueElementFromBulletLayout(keyValue: any, element: any) {
        const keyFigureValue = this.getKeyFigureValue(
            keyValue,
            element.hasOwnProperty('value') ? element.value : element
        );
        return Object.assign({}, this.getKeyFigureElement(keyValue), {
            value: keyFigureValue,
            values: [keyFigureValue]
        });
    }

    getCssClass(
        cellClassRules: string[],
        suffix: string = 'label',
        contextCssClass?: string
    ): string[] {
        if (contextCssClass) {
            return [...cellClassRules, contextCssClass];
        } else {
            const cssClass =
                this.context.layout === 'horisontal'
                    ? `key-figure-default-${suffix}`
                    : `key-figure-cell-${suffix}`;
            return [...cellClassRules, cssClass];
        }
    }
    onClick(event, routingConfig: RoutingConfig, element) {
        if (routingConfig && routingConfig.targetUrl) {
            const params = {};
            routingConfig.queryParameters.forEach((qp) => {
                params[qp.queryParamName] =
                    getObject(qp.sourcePath, element) ||
                    get(element, qp.sourcePath) ||
                    qp.defaultValue;
            });
            this.router.navigate([routingConfig.targetUrl], { queryParams: params });
        } else {
            console.warn('No routingconfig provided - navigation cancelled.');
        }
    }

    toggleShortNumberFormat() {
        this.useShortNumberFormat = !this.useShortNumberFormat;
        this.changeDetectorRef.markForCheck();
    }

    groupData$ = this.activatedRoute.queryParams.pipe(
        switchMap((params) => {
            if (params.legalEntityId) {
                return this._ownerQuery.selectOwnerById$(+params.legalEntityId).pipe(
                    map((legalEntity) => {
                        return {
                            displayName: legalEntity.shortName || legalEntity.longName,
                            website: legalEntity?.metadata?.website,
                            logoUrl: legalEntity?.metadata?.logoUrl,
                            countryCode: legalEntity?.addressCountryCode,
                            icon:
                                legalEntity?.legalType === 'PERSON'
                                    ? 'user-circle'
                                    : 'building-factory-2'
                        };
                    })
                );
            } else if (params.issuerId) {
                return this._accountIssuerQuery.selectIssuerById$(+params.issuerId).pipe(
                    map((issuer) => {
                        return {
                            displayName:
                                issuer.accountIssuerShortName ||
                                issuer.accountIssuerLongName,
                            website: issuer?.metadata?.website,
                            logoUrl: issuer?.metadata?.logoUrl
                        };
                    })
                );
            } else if (params.tagGroup && params.tag) {
                // TODO: this needs to be refactored since the tag model has changed
                return this._tagsQuery.selectByTitle$(params.tag, params.tagGroup).pipe(
                    map((tag) => {
                        return {
                            displayName:
                                tag?.title || this.translateService.instant('NOT_TAGGED'),
                            icon: tag?.icon || 'tag'
                        };
                    })
                );
            } else if (params.accountIds) {
                return combineLatest([
                    this._accountsQuery.selectAccountsByFilter$({
                        accountId: {
                            in: params.accountIds
                                .split(',')
                                .map((accountIdString) => +accountIdString)
                        }
                    }),
                    this._accountIssuerQuery.allAsObject$
                ]).pipe(
                    map(([accounts, issuers]) => {
                        // if multiple accounts...
                        if (accounts.length > 1) {
                            // ...return info for group of accounts
                            return {
                                displayName: 'Multiple accounts',
                                website: null,
                                logoUrl: null,
                                countryCode: null,
                                icon: 'building-bank'
                            };
                        } else {
                            // ...if not, return info for single account
                            const account = accounts[0];
                            const issuer = issuers[account.accountIssuerId];
                            const displayName =
                                account.accountDisplayName ||
                                account.accountClientAlias ||
                                account.accountShortName ||
                                account.accountLongName;
                            return {
                                displayName,
                                website: issuer?.metadata?.website,
                                logoUrl: issuer?.metadata?.logoUrl,
                                countryCode: account?.currency,
                                icon:
                                    !issuer?.metadata?.website &&
                                    !issuer?.metadata?.logoUrl
                                        ? 'building-bank'
                                        : null,
                                accountNumber:
                                    account.accountNumber !== displayName
                                        ? account.accountNumber
                                        : null
                            };
                        }
                    })
                );
            }
            return of(null);
        })
    );
    accounts$ = this._accountsQuery.accountsAsObject$;
    issuers$ = this._accountIssuerQuery.allAsObject$;

    viewSubAccount(event: MouseEvent, id: Acct['accountId']) {
        /*event.preventDefault();
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
            this.router.navigate(['sb-grid', 'portfolio-summary'], {
                queryParams: { accountIds: id }
            })
        );*/
    }
}
